import { Fragment, useEffect, useState } from 'react';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { LandingNavbar } from '@app/landing/landing-navbar';
import "swiper/css/pagination";
import { color, motion } from "framer-motion";
import { Sparkles } from 'lucide-react';
import { LucideIcon } from "lucide-react";
import { Building2 , Shield,  FileCheck, Palette , HardDrive , Database, Eye,SlidersHorizontal  } from 'lucide-react';
import { ShieldCheck,Lock, Zap,Users,Search,Headphones,LayoutDashboard,KeyRound,Cloud } from "lucide-react";

import { useThemeSelector } from '@ui/themes/theme-selector-context';
export function AboutUs() {

  const [columns, setColumns] = useState<number>(4);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setColumns(2);
      } else if (window.innerWidth < 1024) {
        setColumns(2);
      } else {
        setColumns(4);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const stripStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    background: "#ffffffe9",
    borderRadius: "16px",
    boxShadow: "0 4px 40px rgba(0,80,200,0.10)",
    border: "1px solid #e4edff",
    maxWidth: "860px",
    margin: "0 auto",
    overflow: "hidden",
  };

  const itemStyle: React.CSSProperties = {
    padding: "28px 20px",
    textAlign: "center",
  };

  const numberStyle: React.CSSProperties = {
    fontFamily: "'Poppins', sans-serif",
    fontSize: "30px",
    fontWeight: 800,
    color: "#0072ff",
    lineHeight: 1,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "12px",
    color: "#7a8fa6",
    marginTop: "6px",
    fontWeight: 500,
  };

  const stats = [
    { num: "10K+", label: "Trusted Users Worldwide" },
    { num: "99.9%", label: "Uptime Guaranteed" },
    { num: "24/7", label: "Support" },
    { num: "₹20/mo", label: "Plans Starting From" },
  ];

  const features: {
      icon: LucideIcon;
      title: string;
      text: string;
    }[] = [
    {
      icon: ShieldCheck,
      title: "Your Data Fully Protected",
      text: "Security is built into everything we create. With advanced encryption and continuous protection, your files stay private, secure, and fully under your control.",
    },
    {
      icon: Zap,
      title: "Fast Performance",
      text: "Upload, download, and sync your files in the cloud without delays. Every millisecond counts, so your data is always ready when you need it.",
    },
    {
      icon: Headphones,
      title: "Dedicated Support",
      text: "Our support team is here whenever you need help, offering clear guidance and real solutions to keep your storage experience simple and stress-free.",
    },
    {
      icon: LayoutDashboard,
      title: "Simple & User-Friendly",
      text: "We make cloud storage simple and secure, so you can upload, organize, and access your files without confusion.",
    },
    {
      icon: KeyRound,
      title: "Full Data Ownership",
      text: "Your data belongs to you at all times. Move, export, or delete your files anytime with complete freedom and transparency.",
    },
    {
      icon: Cloud,
      title: "Always Available",
      text: "Your files are always accessible, securely stored, and ready whenever you need them, giving you complete peace of mind.",
    },
  ];

interface Card {
  icon: LucideIcon;
  title: string;
  description: string;
}
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cards: Card[] = [
  {
    icon: Lock,
    title: "End-to-End Encryption",
    description: "Secure files with private encryption",
  },
  {
    icon: Zap,
    title: "Fast Uploads & Downloads",
    description: "Quickly store and retrieve files",
  },
  {
    icon: SlidersHorizontal,
    title: "Control Without Complexity",
    description: "Powerful permissions made simple",
  },
  {
    icon: Search,
    title: "Smart Search",
    description: "Find files instantly and easily",
  },
];

type ColorKey = "sky" | "violet" | "emerald" | "amber";

const useCases: {
  icon: React.ElementType;
  title: string;
  desc: string;
  color: ColorKey;
}[] = [
  {
    icon: HardDrive ,
    title: "Personal Storage",
    desc: "Individuals needing a safe place for personal files, photos, and documents.",
    color: "sky",
  },
  {
    icon: Users,
    title: "Team Collaboration",
    desc: "Businesses requiring encrypted and collaborative storage for teams.",
    color: "violet",
  },
  {
    icon: Palette ,
    title: "creators",
    desc: "Content creators (YouTubers, filmmakers, photographers) managing large files.",
    color: "emerald",
  },
  {
    icon: Building2 ,
    title: "Enterprises",
    desc: "Enterprises with complex, multi-location data backup and sync needs.",
    color: "amber",
  },
];

const colorMap: Record<
  ColorKey,
  {
    border: string;
    shadow: string;
    iconBg: string;
    iconBorder: string;
    iconColor: string;
    accent: string;
  }
> = {
  sky: {
    border: "rgba(14,165,233,0.30)",
    shadow: "rgba(14,165,233,0.20)",
    iconBg: "rgba(14,165,233,0.09)",
    iconBorder: "rgba(14,165,233,0.20)",
    iconColor: "#0284c7",
    accent: "#38bdf8",
  },
  violet: {
    border: "rgba(124,58,237,0.30)",
    shadow: "rgba(124,58,237,0.20)",
    iconBg: "rgba(124,58,237,0.09)",
    iconBorder: "rgba(124,58,237,0.20)",
    iconColor: "#6d28d9",
    accent: "#818cf8",
  },
  emerald: {
    border: "rgba(5,150,105,0.30)",
    shadow: "rgba(5,150,105,0.20)",
    iconBg: "rgba(5,150,105,0.09)",
    iconBorder: "rgba(5,150,105,0.20)",
    iconColor: "#047857",
    accent: "#34d399",
  },
  amber: {
    border: "rgba(217,119,6,0.30)",
    shadow: "rgba(217,119,6,0.20)",
    iconBg: "rgba(217,119,6,0.09)",
    iconBorder: "rgba(217,119,6,0.20)",
    iconColor: "#b45309",
    accent: "#fbbf24",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.21, 0.45, 0.32, 0.9] },
  },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

  const { selectThemeTemporarily } = useThemeSelector();
  useEffect(() => {
    selectThemeTemporarily('light');
    return () => selectThemeTemporarily(null);
  }, [selectThemeTemporarily]);

  return (
    <Fragment>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary  shadow-lg"
        menuPosition="homepage-navbar"
      />
      
      <section className=" relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>
        <div
          className={`landing-container z-10 flex flex-col items-center justify-between  pt-100 lg:flex-row`}
        >
          <div className="mx-auto w-full max-w-6xl px-4 text-center">

            {/* Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Secure Cloud Storage Built {" "}  
              <br />
              for 
              <span className="color-primary-gradient"> The Way You Work </span>

            </h1>

            {/* Sub heading */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px]  text-[#6b7280]">
              We are building secure, scalable, and user-centric cloud storage solutions for individuals,
               businesses, and creators around the globe.
            </p>

          <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
            <a
              href="/register"
              className="inline-flex items-center justify-center 
              px-32 py-10 
              text-white font-bold
              bg-gradient-to-r from-[#0072ff] to-[#00c6ff]
              shadow-lg transition-all duration-300
              hover:from-[#0059d6] hover:to-[#00aaff]
              hover:shadow-2xl hover:-translate-y-1"
              style={{
              borderRadius: "12px"
            }}
            >
              Get 25 GB FREE
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center justify-center
              px-30 py-8 font-bold
              text-[#0072ff]
              relative transition-all duration-300
              hover:-translate-y-1 hover:shadow-lg"
              style={{
                borderRadius: "12px",
                background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                border: "2px solid transparent"
              }}
              >
              View Pricing
            </a>
          </div>
          </div>
        </div>
    <div style={{ padding: "20px" }}>
      <div style={stripStyle}>
        {stats.map((stat, index) => (
          <div
            key={index}
            style={{
              ...itemStyle,
              borderRight:
                columns === 4 && index !== stats.length - 1
                  ? "1px solid #e8f0ff"
                  : "none",
              borderBottom:
                columns !== 4 ? "1px solid #e8f0ff" : "none",
            }}
          >
            <div style={numberStyle}>{stat.num}</div>
            <div style={labelStyle}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
      </section> 

      <section className="relative overflow-hidden py-20 md:py-32 bg-white">
      <div className="landing-container z-10 "
      style={{
        padding: "80px 20px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "70px",
          alignItems: "center",
        }}
      >
        
                {/* LEFT SIDE */}
          <div className="relative w-full flex justify-center">
            <div className="relative w-full max-w-[520px]">

              {/* Background Offset Block */}
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />

              {/* Image */}
              <img
                src="/images/aboutus/about.webp"
                alt="About Us"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

        {/* Right SIDE */}
        <div>

          <h2 className="animate-fade-down font-archivo mt-10 text-3xl font-bold md:text-4xl lg:text-5xl">
              Who We Are 
              
              </h2>
              <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                At SpaceByte, we are a passionate team of developers, engineers, and cloud experts committed 
                to building a <span className="color-primary-gradient font-bold"> reliable, secure, and affordable cloud 
                storage solution </span>  for individuals, businesses, creators, and enterprises across people and
                beyond. With a mission to make <span className="color-primary-gradient font-bold"> cloud storage simple and accessible </span> , 
                we are redefining the way people stores, manages, and shares digital data.
              </p>
        </div>
      </div>
    </div>
      </section>

      <section  className="relative overflow-hidden py-20 md:py-32 bg-[#f5f8ff] "
        style={{
          padding: "100px 24px",
          background: "#f8fafc",
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        <div className="landing-container z-10" style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Header */}
          <div className="text-center mb-14">
                <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
                What We Do 
                <br /> 
                </h2>
                <p className="color-gray font-inter animate-fade-down text-base py-10 md:py-14">
                    We provide<span className="color-primary-gradient font-bold">  scalable, high-performance cloud storage </span> services designed for:
                </p> 
            </div>

          {/* Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px,1fr))",
              gap: 18,
            }}
          >
            {useCases.map((uc) => {
              const c = colorMap[uc.color];
              return (
                <div
                  key={uc.title}
                  style={{
                    background: "#fff",
                    border: `1px solid #e2e8f0`,
                    borderRadius: 20,
                    padding: "30px 26px",
                    transition: "all .3s ease",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = c.border;
                    e.currentTarget.style.boxShadow = `0 18px 48px -8px ${c.shadow}`;
                    e.currentTarget.style.transform = "translateY(-5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.boxShadow =
                      "0 2px 6px rgba(0,0,0,0.04)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 13,
                      background: c.iconBg,
                      border: `1px solid ${c.iconBorder}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 18,
                    }}
                  >
                    <uc.icon size={22} color={c.iconColor} />
                  </div>

                  <h3
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginBottom: 8,
                      color: "#0f172a",
                    }}
                  >
                    {uc.title}
                  </h3>

                  <p style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6 }}>
                    {uc.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-20 md:py-32 bg-white">
        <div className="landing-container z-10 "
      style={{
        padding: "30px 20px",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "70px",
          alignItems: "center",
        }}
      >
        {/* LEFT SIDE */}
        <div>
          <h2 className="animate-fade-down font-archivo mt-10 text-3xl font-bold md:text-4xl lg:text-5xl">
              We protect what 
              <br />
                <span className="color-primary-gradient"> matters most to you</span>
              
              </h2>
              <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                SpaceByte was created to simplify how individuals and teams store, manage,
                and access their digital files. We empower you with dependable cloud storage built around privacy,
                reliability, and everyday convenience. Our mission is to make file management secure, stress-free,
                and accessible, giving you peace of mind in a digital world.
              </p>

        </div>

        {/* RIGHT SIDE */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "14px",
            marginTop: "14px",
          }}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              style={{
                background: "#f5f9ff",
                border: "1px solid #ddeaff",
                borderRadius: "14px",
                padding: "20px",
                transition: "all 0.25s ease",
                transform:
                  hoverIndex === index
                    ? "translateY(-3px)"
                    : "translateY(0px)",
                boxShadow:
                  hoverIndex === index
                    ? "0 8px 28px rgba(0,112,255,0.10)"
                    : "none",
                cursor: "pointer",
              }}
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <div style={{ fontSize: "22px", marginBottom: "8px" }}>
                <card.icon size={28} strokeWidth={2} className="text-[#0072ff]" />
              </div>

              <strong
                style={{
                  display: "block",
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#0d0d2b",
                  marginBottom: "4px",
                }}
              >
                {card.title}
              </strong>

              <span
                style={{
                  fontSize: "12px",
                  color: "#7a8fa6",
                }}
              >
                {card.description}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
      </section>

      <section className="bg-[#f5f8ff] py-[80px] relative overflow-hidden ">
      <div className="landing-container z-10 max-w-[1140px] mx-auto px-[20px]">
        {/* Header */}
          <div className="text-center mb-14">
              <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              The Principles Behind Our 
              <br />
                <span className="color-primary-gradient"> Secure Cloud Storage</span>
              
              </h2>
              {/* <p className="color-gray font-inter animate-fade-down py-16 md:py-24">
                SpaceByte offers content creators 
                <span className="color-primary-gradient font-bold"> secure, fast, and scalable cloud storage</span>.
                Easily store, manage,  and share large videos, high-resolution images, and audio files
                  while collaborating seamlessly and focusing on your creative workflow.
              </p> */}
          </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px] mt-[52px]">
          {features.map((item, index) => (
            <WhyCard
              key={index}
              item={item}
            />
          ))}
        </div>
      </div>
      </section>

      <section id="security" className="py-30 md:py-40 bg-gradient-to-br from-white to-[#F8FAFC] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                {/* Main Shield */}
                <div className="w-full max-w-md mx-auto aspect-square relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-3xl opacity-10 blur-3xl"></div>
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="w-200 h-200 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center shadow-2xl">
                      <Shield className="w-100 h-100 text-white" />
                    </div>
                  </div>

                  {/* Orbiting Icons */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0"
                  >
                    {[Lock, Database, FileCheck, Users, Eye].map((Icon, index) => {
                      const angle = (index * 360) / 5;
                      const radius = 140;
                      const x = Math.cos((angle * Math.PI) / 180) * radius;
                      const y = Math.sin((angle * Math.PI) / 180) * radius;

                      return (
                        <motion.div
                          key={index}
                          animate={{ rotate: -360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                          className="absolute top-1/2 left-1/2 w-40 h-40 bg-white rounded-xl shadow-lg flex items-center justify-center"
                          style={{
                            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                          }}
                        >
                          <Icon className="w-20 h-20 text-[#3B82F6]" />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Right - Features List */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              
            <div className="text-center mb-14">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold text-start md:text-4xl lg:text-5xl">
            Our Vision
            <br /> 
            </h2>
            <p className="color-gray font-inter animate-fade-down text-xl text-start py-10 md:py-14">
                To become people's<span className="color-primary-gradient font-bold">  most trusted cloud storage provider </span> 
                empowering users with <span className="color-primary-gradient font-bold">  digital independence, secure data ownership, </span>
                and <span className="color-primary-gradient font-bold"> technology that scales with their needs.   </span>
            </p> 
        </div>
        
            </motion.div>
          </div>
        </div>
      </section>


      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white"
            >
              Ready to Secure Your Data?
            </motion.h2>

            {/* Subheading */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto"
            >
              SpaceByte Cloud Storage is a comprehensive cloud storage solution that guarantees zero-knowledge encryption for businesses and individuals. Join thousands of users trusting our secure infrastructure to protect critical data, ensuring your files remain private, safe from ransomware, and accessible anytime.
            </motion.p>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>

            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-20 flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm"
            >

            </motion.div>
          </motion.div>
        </div>
      </section>

      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0" aria-label="SpaceByte Homepage">
                  <img
                    src="/images/logo-light.png"
                    alt="Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat,
                    India - 382345</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    Mo. +91 97257 71457
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho " aria-label="SpaceByte Instagram Profile">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B" aria-label="SpaceByte YouTube Channel">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start ">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors text-start">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/Pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
                <li>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className='py-8 md:py-20 px-30'>
              <h2 className="font-semibold text-xl mb-10  text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
                {/* <li>
                <a href="https://spacebyte.in/api-docs" className="hover:text-white transition-colors">
                  Developers
                </a>
              </li> */}
                <li>
                  <a href="/storage/sitemaps/sitemap-index.xml" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className='py-8 md:py-20 px-30'>
              <h3 className="font-semibold text-xl mb-10  text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/pages/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}


function WhyCard({
  item,
}: {
  item: {
    icon: LucideIcon;
    title: string;
    text: string;
  };
}) {
  const { icon: Icon, title, text } = item;

  return (
    <div
      className="
        relative overflow-hidden rounded-[16px] border
        p-[32px_28px]
        transition-all duration-500
        ease-[cubic-bezier(0.4,0,0.2,1)]
        hover:-translate-y-[6px]
        hover:shadow-[0_16px_40px_rgba(0,112,255,0.12)]
      "
      style={{
        background: "#ffffff",
        borderColor: "#e4edff",

        /* ✅ Top Gradient Line (Best Professional Way) */
        backgroundImage:
          "linear-gradient(90deg, #0070ff, #00c6ff)",
        backgroundSize: "100% 4px",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
      }}
    >
      {/* Icon */}
      <div
        className="w-[50px] h-[50px] rounded-[12px] flex items-center justify-center text-[22px] mb-[18px]"
        style={{
          background: "#eaf3ff",
        }}
      >
        <Icon size={22} strokeWidth={2} className="text-[#0070ff]" />
      </div>

      {/* Title */}
      <h3 className="text-[16px] font-bold text-[#0d0d2b] mb-[10px]">
        {title}
      </h3>

      {/* Text */}
      <p className="text-[13.5px] text-[#7a8fa6] leading-[1.7]">
        {text}
      </p>
    </div>
  );
}